import { Injectable } from '@angular/core';

import { Observable, of, throwError } from 'rxjs';
import { map, retry, switchMap } from 'rxjs/operators';

import {
    ExportTagType,
    PaginatedResults,
    PartialRecord,
    ShAbstractRecordDownloadService
} from '@stylehaven/components';
import { CustomErrorType } from '../../entities/common/custom-error-type.entity';
import { DownloadSearch } from '../../entities/downloads/download-search.entity';
import { ExportEntity } from '../../entities/downloads/export-entity.entity';
import { ExportJob } from '../../entities/downloads/export-job.entity';
import { ExportRequest } from '../../entities/downloads/export-request.entity';
import { ExportEventType } from '../../entities/downloads/types/export-event.type';
import { ApiListToPaginatedResults, MapObjectToHttpParams, waitForIndex } from '../../helpers';
import { ApiList } from '../../record/entities/api-list.entity';
import { AbstractHttpService } from '../../services/http/http-service.abstract';

@Injectable()
export class DownloadService implements ShAbstractRecordDownloadService {
    private readonly ENDPOINT: string = 'exports';

    constructor(private readonly http: AbstractHttpService) {}

    getAll(downloadSearch: DownloadSearch): Observable<PaginatedResults<ExportEntity>> {
        return this.http
            .get<ApiList<ExportEntity>>(this.ENDPOINT, MapObjectToHttpParams(downloadSearch), 'v2')
            .pipe(map(apiList => ApiListToPaginatedResults(apiList, downloadSearch.nrOfResults)));
    }

    getOne(exportJobId: string): Observable<ExportJob> {
        return this.http.get<ExportJob>(`${this.ENDPOINT}/${exportJobId}`);
    }

    request(exportRequest: ExportRequest): Observable<ExportJob[]> {
        return this.http.post(this.ENDPOINT, exportRequest);
    }

    getDownloadUrlForAccessRepresentation(record: PartialRecord): Observable<string | null> {
        const representationAsExportSubject: ExportRequest = {
            SubjectId: [`${record.getRecordId()}`],
            Options: {
                Metadata: {
                    Include: false
                }
            },
            EventType: ExportEventType.Consultation,
            Tag: ExportTagType.Consultation
        };

        return this.request(representationAsExportSubject).pipe(
            retry(1),
            switchMap(insertedExportJobs => this.waitForExportJobCompletion(insertedExportJobs))
        );
    }

    private waitForExportJobCompletion(insertedExportJobs: ExportJob[]): Observable<string | null> {
        return this.getOne(insertedExportJobs[0].Id).pipe(
            switchMap(exportJob => {
                const downloadUrl = exportJob.DownloadUrl;

                if (downloadUrl) {
                    return of(downloadUrl);
                } else {
                    return throwError(CustomErrorType.ExportTimeout);
                }
            }),
            waitForIndex()
        );
    }
}
